// Belege der Finanzen per E-Mail (docs/anmeldung/SPEZIFIKATION.md §5.1, §5.4, §8.2): Rechnung und Rechnungskorrektur
// einzeln oder als Sammelversand (Rechnungslauf), Mahnung einzeln oder als Mahnlauf. Merxo erzeugt kein PDF – der
// Beleg steht im Mailtext, die E-Rechnung (XRechnung) hängt als Datei an. Versendet wird erst nach dem Commit (und
// nach dem Rückgängig-Fenster); bis dahin bietet die Aktion „Rückgängig“ an (platform.mail.cancel).
//
// Zu jeder Sendeaktion gibt es eine lesende Vorschau mit derselben Eingabe (…Preview): Vorschlag der Empfänger,
// gerenderte Nachricht, Anhänge, Hinweise – für den Senden-Dialog. Sammelversand: …Targets zählt vorab, was per
// E-Mail hinausgeht und was zum Drucken bleibt.
import { defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { zMailMessageText } from '@shared/mail'
import { documentMailInputShape, zMailBatchResult, zMailBatchTargets, zMailPreviewResult, zMailQueuedResult, zMailRecipients } from '@shared/mailSend'
import { z } from 'zod'

export const FINANCE_MAIL_PERMISSIONS = { invoice: 'finance.invoice.send', dunning: 'finance.dunning.send' } as const

const zInvoiceMailInput = z.strictObject({
  ...documentMailInputShape,
  /** die beim Festschreiben abgelegte E-Rechnung anhängen (Standard, wenn es eine gibt) */
  includeEInvoice: z.boolean().default(true),
  /** Ohne E-Rechnung im Anhang: Der Kunde ist mit Rechnungen per E-Mail einverstanden (sonstige Rechnung) */
  consentConfirmed: z.boolean().optional(),
})

const INVOICE_LLM =
  'Nur festgeschriebene Belege. Ohne `to` geht die E-Mail an die hinterlegte Adresse (E-Rechnungs-Adresse aus dem Beleg, ' +
  'sonst die E-Mail des Kunden). Die E-Rechnung (XRechnung) hängt an, wenn es eine gibt. Ohne E-Rechnung im Anhang ist ' +
  '`consentConfirmed: true` nötig (Zustimmung des Kunden zu Rechnungen per E-Mail). Persönliche Nachricht höchstens ' +
  '1000 Zeichen. Versand nach dem Rückgängig-Fenster. Beispiel: „Schick Gasthof Post die Rechnung von gestern“.'

export const invoiceSend = defineContract({
  name: 'finance.invoice.send',
  title: 'Rechnung per E-Mail senden',
  description: 'Sendet eine festgeschriebene Rechnung per E-Mail – der Beleg steht im Mailtext, die E-Rechnung hängt an.',
  llm: { description: `Sendet EINE festgeschriebene Rechnung per E-Mail an den Kunden. ${INVOICE_LLM}`, keywords: ['Rechnung schicken', 'Rechnung per E-Mail', 'Rechnung mailen', 'XRechnung senden', 'Rechnung versenden'] },
  input: zInvoiceMailInput,
  output: zMailQueuedResult,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'write',
  scope: 'record',
  effects: { external: true, reversible: true },
})

export const invoiceSendPreview = defineContract({
  name: 'finance.invoice.sendPreview',
  title: 'Rechnung per E-Mail – Vorschau',
  description: 'Zeigt, wie die E-Mail mit der Rechnung aussieht: Empfänger-Vorschlag, Betreff, Inhalt und Anhänge.',
  llm: { description: 'Zeigt die E-Mail zu einer Rechnung, bevor sie versendet wird (Empfänger-Vorschlag, Betreff, Anhänge). Verändert nichts. Versenden: finance.invoice.send.', keywords: ['Rechnung per E-Mail Vorschau'] },
  input: zInvoiceMailInput,
  output: zMailPreviewResult,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'read',
  scope: 'record',
})

export const creditNoteSend = defineContract({
  name: 'finance.creditNote.send',
  title: 'Rechnungskorrektur per E-Mail senden',
  description: 'Sendet eine festgeschriebene Rechnungskorrektur per E-Mail – der Beleg steht im Mailtext, die E-Rechnung hängt an.',
  llm: { description: `Sendet EINE festgeschriebene Rechnungskorrektur (Gutschrift) per E-Mail an den Kunden. ${INVOICE_LLM}`, keywords: ['Gutschrift schicken', 'Rechnungskorrektur per E-Mail', 'Gutschrift mailen'] },
  input: zInvoiceMailInput,
  output: zMailQueuedResult,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'write',
  scope: 'record',
  effects: { external: true, reversible: true },
})

export const creditNoteSendPreview = defineContract({
  name: 'finance.creditNote.sendPreview',
  title: 'Rechnungskorrektur per E-Mail – Vorschau',
  description: 'Zeigt, wie die E-Mail mit der Rechnungskorrektur aussieht: Empfänger-Vorschlag, Betreff, Inhalt und Anhänge.',
  llm: { description: 'Zeigt die E-Mail zu einer Rechnungskorrektur, bevor sie versendet wird. Verändert nichts. Versenden: finance.creditNote.send.', keywords: [] },
  input: zInvoiceMailInput,
  output: zMailPreviewResult,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'read',
  scope: 'record',
})

const zDocumentIds = z.array(zId).min(1, { error: 'Bitte mindestens eine Rechnung wählen.' }).max(200, { error: 'Höchstens 200 Rechnungen auf einmal.' })

export const invoiceSendTargets = defineContract({
  name: 'finance.invoice.sendTargets',
  title: 'Rechnungen per E-Mail – wer bekommt was',
  description: 'Zählt vor dem Sammelversand, welche Rechnungen per E-Mail hinausgehen und welche zum Drucken bleiben.',
  llm: { description: 'Zeigt für bis zu 200 Rechnungen, an welche Adresse jede per E-Mail ginge und welche zum Drucken bleiben (ohne Adresse, schon versandt). Verändert nichts. Versenden: finance.invoice.sendMany.', keywords: ['Rechnungen per E-Mail', 'wer hat eine E-Mail-Adresse'] },
  input: z.strictObject({ documentIds: zDocumentIds }),
  output: zMailBatchTargets,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'read',
  scope: 'record',
})

export const invoiceSendMany = defineContract({
  name: 'finance.invoice.sendMany',
  title: 'Rechnungen per E-Mail versenden',
  description: 'Sendet mehrere festgeschriebene Rechnungen an die hinterlegten Adressen – je Rechnung eine E-Mail; Rechnungen ohne Adresse bleiben zum Drucken.',
  llm: {
    description:
      'Sammelversand (Rechnungslauf): je Rechnung EINE E-Mail an die hinterlegte Adresse des Kunden, mit E-Rechnung im ' +
      'Anhang, wenn es eine gibt. Rechnungen ohne Adresse, schon versandte und – ohne `consentConfirmed` – Rechnungen ohne ' +
      'E-Rechnung bleiben zum Drucken und stehen im Ergebnis. Erst finance.invoice.sendTargets zeigen.',
    keywords: ['Rechnungslauf versenden', 'alle Rechnungen mailen', 'Sammelversand Rechnungen'],
  },
  input: z.strictObject({
    documentIds: zDocumentIds,
    /** gilt für Rechnungen ohne E-Rechnung im Anhang: Die Kunden sind mit Rechnungen per E-Mail einverstanden */
    consentConfirmed: z.boolean().optional(),
  }),
  output: zMailBatchResult,
  permission: FINANCE_MAIL_PERMISSIONS.invoice,
  risk: 'write',
  scope: 'record',
  effects: { external: true },
  assistant: 'opt-in',
  limits: { maxItems: 200, itemCount: (input) => input.documentIds.length },
})

const zDunningMailInput = z.strictObject({
  noticeId: zId,
  to: zMailRecipients.optional(),
  cc: zMailRecipients.optional(),
  message: zMailMessageText.nullish(),
})

export const dunningSendNotice = defineContract({
  name: 'finance.dunning.sendNotice',
  title: 'Mahnung per E-Mail senden',
  description: 'Sendet eine bestätigte Mahnung per E-Mail – mit Mahnstufe, Posten, Gebühr und Zahlungsfrist im Mailtext.',
  llm: {
    description:
      'Sendet EINE bestätigte Mahnung (aus finance.dunning.notices) per E-Mail an den Kunden. Ohne `to` an die hinterlegte ' +
      'Adresse (E-Rechnungs-Adresse, sonst E-Mail des Kunden). Zurückgenommene Mahnläufe und gesperrte Kunden oder Posten ' +
      'werden nicht versendet. Persönliche Nachricht höchstens 1000 Zeichen.',
    keywords: ['Mahnung schicken', 'Mahnung per E-Mail', 'Zahlungserinnerung mailen'],
  },
  input: zDunningMailInput,
  output: zMailQueuedResult,
  permission: FINANCE_MAIL_PERMISSIONS.dunning,
  risk: 'write',
  effects: { external: true, reversible: true },
})

export const dunningSendPreview = defineContract({
  name: 'finance.dunning.sendPreview',
  title: 'Mahnung per E-Mail – Vorschau',
  description: 'Zeigt, wie die E-Mail mit der Mahnung aussieht: Empfänger-Vorschlag, Betreff und Inhalt.',
  llm: { description: 'Zeigt die E-Mail zu einer Mahnung, bevor sie versendet wird. Verändert nichts. Versenden: finance.dunning.sendNotice.', keywords: [] },
  input: zDunningMailInput,
  output: zMailPreviewResult,
  permission: FINANCE_MAIL_PERMISSIONS.dunning,
  risk: 'read',
})

export const dunningSendTargets = defineContract({
  name: 'finance.dunning.sendTargets',
  title: 'Mahnlauf per E-Mail – wer bekommt was',
  description: 'Zählt vor dem Versand eines Mahnlaufs, welche Mahnungen per E-Mail hinausgehen und welche zum Drucken bleiben.',
  llm: { description: 'Zeigt für einen Mahnlauf, an welche Adresse jede Mahnung per E-Mail ginge und welche zum Drucken bleiben. Verändert nichts. Versenden: finance.dunning.sendRun.', keywords: ['Mahnlauf per E-Mail'] },
  input: z.strictObject({ runId: zId }),
  output: zMailBatchTargets,
  permission: FINANCE_MAIL_PERMISSIONS.dunning,
  risk: 'read',
})

export const dunningSendRun = defineContract({
  name: 'finance.dunning.sendRun',
  title: 'Mahnlauf per E-Mail versenden',
  description: 'Sendet die Mahnungen eines bestätigten Mahnlaufs an die hinterlegten Adressen; Mahnungen ohne Adresse bleiben zum Drucken.',
  llm: {
    description:
      'Versendet alle Mahnungen eines bestätigten Mahnlaufs per E-Mail – je Mahnung eine E-Mail an die hinterlegte Adresse. ' +
      'Ohne Adresse, schon versandt oder gesperrt: bleibt zum Drucken und steht im Ergebnis. Erst finance.dunning.sendTargets zeigen.',
    keywords: ['Mahnlauf versenden', 'Mahnungen mailen', 'alle Mahnungen per E-Mail'],
  },
  input: z.strictObject({ runId: zId }),
  output: zMailBatchResult,
  permission: FINANCE_MAIL_PERMISSIONS.dunning,
  risk: 'write',
  effects: { external: true },
  assistant: 'opt-in',
})
